import { useCallback } from "react";

import {
  SWRHooksConfig,
  usePromise,
  useSWRHooksConfig,
} from "@touchtech/web-hooks";

import { BasketItemRequest } from "@is/hooks/basket/useManageBasket";
import { useManageApiHeaders } from "@is/hooks/useManageApiHeaders";
import { api, createBasketUpdateUrl, createBasketUrl } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { findQuantityChanges } from "@is/utils";

type Params = {
  items: BasketItemRequest[];
  basketItems?: BasketItemRequest[];
};

type BasketUpdateState = {
  isPending: boolean;
  error: Error | null;
  data: BasketItemRequest[] | null;
};

async function handleBasketUpdate(
  swrConfig: SWRHooksConfig,
  sessionId: string | null,
  params: Params
): Promise<BasketItemRequest[]> {
  const updateBasketUrl = createBasketUpdateUrl();
  const basketUrl = createBasketUrl();

  const { items, basketItems } = params;

  const requestBasketItems = items.map(
    ({ ean, productId, price, quantity }) => ({
      ean,
      productId,
      price,
      quantity,
    })
  );

  try {
    const data = (await api.put(updateBasketUrl, {
      items: requestBasketItems,
    })) as BasketItemRequest[];

    if (!!basketItems && !!items) {
      const quantityChanges = findQuantityChanges(basketItems, items);

      quantityChanges.forEach(({ ean, change, styleId, variantId }) => {
        trackEvent({
          eventType: TrackingEventTypes.BasketQuantityChanged,
          data: { ean, quantity: change, styleId, variantId },
        });
      });
    }

    if (sessionId) {
      swrConfig.mutate([basketUrl, sessionId]);
    }

    return data;
  } catch (e) {
    //
  }
}

export const useBasketUpdate = (): [
  BasketUpdateState,
  (params: Params) => Promise<BasketItemRequest[]>
] => {
  const [{ isPending, error, data }, dispatch] =
    usePromise<BasketItemRequest[]>();

  const swrConfig = useSWRHooksConfig();
  const { sessionId } = useManageApiHeaders();

  const updateBasket = useCallback(
    (params: Params) =>
      dispatch(handleBasketUpdate(swrConfig, sessionId, params)),
    [dispatch, swrConfig, sessionId]
  );

  return [{ isPending, error, data }, updateBasket];
};
